import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from 'redux/actions';
import { cacheKey, IDLE_TIME } from 'appConstants';
import { session } from 'util/session';

const events = ['mousemove', 'mousedown', 'touchstart', 'touchmove', 'onclick', 'keydown'];

/**
 * This custom hook will add "interactive events" to the window if:
 * 1. User has a session token; i.e. not a long lived token, and
 * 2. User is logged in.
 *
 * If user has been inactive for > 15 mins, it will be logged out,
 * otherwise the timer will reset since most recent activity
 */
export const useInactivity = () => {
  const timerRef = React.useRef(null);
  const dispatch = useDispatch();
  const [inactive, setInactive] = React.useState(false);
  const { authenticated } = useSelector((state) => state.user);

  const hasSessionToken = sessionStorage.getItem(cacheKey.TOKEN);

  const resetTimer = () => {
    // mark session as active
    session.isActive = true;

    // Reset timeout again for inactivity
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      dispatch(logout());
      setInactive(true);
      session.isActive = false;
    }, IDLE_TIME);
  };

  React.useEffect(
    () => {
      // Add interactive events after user has logged in
      if (hasSessionToken && authenticated) {
        events.forEach((e) => window.addEventListener(e, resetTimer));
      }
      // Remove events to prevent memory leaks
      return () => {
        events.forEach((e) => window.removeEventListener(e, resetTimer));
        clearTimeout(timerRef.current);
        timerRef.current = null;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, authenticated],
  );

  return [inactive, setInactive];
};
